/**
 * 结算批次（只读）。
 *
 * 批次由 worker 的结算任务生成，控制台不提供「手工生成/改金额」的入口：
 * `net = gross - fees` 由数据库 CHECK 强制，手工改一个数就会让它与账本分录对不上。
 *
 * 想验证批次金额，看 `/settlements/:id/entries` —— 它按凭证反查记账分录，
 * 是批次金额唯一可验证的来源。
 */

import { useState } from 'react';
import { useAsync, usePagedList } from '../lib/hooks.ts';
import {
  Card, CopyId, ErrorBox, Field, Loading, Modal, Money, Select, StatusBadge,
} from '../components/ui.tsx';
import { DataTable, Pager, type Column } from '../components/table.tsx';
import { formatMinor } from '../lib/money.ts';
import { formatDate, formatDateTime } from '../lib/format.ts';
import type { Payout, SettlementBatch } from '../lib/types.ts';

const BATCH_STATUSES = [
  { value: 'open', label: '累计中' },
  { value: 'closed', label: '已封账' },
  { value: 'settling', label: '结算中' },
  { value: 'settled', label: '已结清' },
  { value: 'cancelled', label: '已取消' },
];

interface BatchDetail extends SettlementBatch {
  readonly payouts: readonly Payout[];
}

interface BatchEntry {
  readonly txn_id: string;
  readonly txn_kind: string;
  readonly occurred_at: string;
  readonly memo: string | null;
  readonly entry_id: string;
  readonly account_id: string;
  readonly owner_org_id: string;
  readonly account_kind: string;
  readonly direction: 'dr' | 'cr';
  readonly amount: string;
  readonly currency: string;
}

export function SettlementsPage() {
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const list = usePagedList<SettlementBatch>('/v1/settlements', {
    status: status === '' ? null : status,
  });

  const columns: readonly Column<SettlementBatch>[] = [
    { key: 'id', header: '批次号', render: (row) => <CopyId id={row.id} /> },
    { key: 'org', header: '组织', render: (row) => row.org_name },
    { key: 'period', header: '结算周期', render: (row) => (
      <span className="cell-muted mono" style={{ fontSize: 11.5 }}>
        {formatDate(row.period_start)} → {formatDate(row.period_end)}
      </span>
    ) },
    { key: 'gross', header: '毛额', numeric: true, render: (row) => (
      <Money value={row.gross} currency={row.currency} showCurrency={false} />
    ) },
    { key: 'fees', header: '手续费', numeric: true, render: (row) => (
      <Money value={row.fees} currency={row.currency} tone="bad" showCurrency={false} />
    ) },
    { key: 'net', header: '应结净额', numeric: true, render: (row) => (
      <Money value={row.net} currency={row.currency} tone="good" />
    ) },
    /*
     * `domain` 必须传：批次的 `closed` 是「已封账」，而全局映射里的 `closed`
     * 是组织的「已关闭（不可逆）」—— 不传就会把正常封账的批次画成红色的「已关闭」。
     */
    { key: 'status', header: '状态', render: (row) => (
      <StatusBadge status={row.status} domain="settlement_batch" />
    ) },
    { key: 'settled', header: '结清时间', render: (row) => (
      <span className="cell-muted mono">{formatDateTime(row.settled_at)}</span>
    ) },
  ];

  return (
    <>
      <h1>结算批次</h1>

      <div className="filters">
        <Field label="状态">
          <Select value={status} onChange={setStatus} placeholder="全部" options={BATCH_STATUSES} />
        </Field>
      </div>

      <Card flush>
        <DataTable
          columns={columns}
          rows={list.items}
          loading={list.loading}
          error={list.error}
          rowKey={(row) => row.id}
          onRowClick={(row) => { setSelected(row.id); }}
          empty="还没有结算批次"
          emptyHint="批次由后台的结算任务按周期自动生成，需要先有已请款并过了结算周期的交易。"
        />
        <Pager
          page={list.page}
          hasNext={list.hasNext}
          hasPrev={list.hasPrev}
          onNext={list.next}
          onPrev={list.prev}
          loading={list.loading}
          count={list.items.length}
        />
      </Card>

      {selected !== null && (
        <BatchModal id={selected} onClose={() => { setSelected(null); }} />
      )}
    </>
  );
}

function BatchModal({ id, onClose }: { id: string; onClose: () => void }) {
  const detail = useAsync<BatchDetail>(`/v1/settlements/${id}`);
  const entries = useAsync<{ data: readonly BatchEntry[] }>(`/v1/settlements/${id}/entries`);

  return (
    <Modal title="结算批次" onClose={onClose} wide>
      {detail.error !== null ? <ErrorBox error={detail.error} />
        : detail.data === null ? <Loading />
        : (
          <>
            <div className="grid cols-3">
              <div className="metric">
                <span className="metric-label">毛额</span>
                <span className="metric-value">{formatMinor(detail.data.gross, detail.data.currency)}</span>
              </div>
              <div className="metric">
                <span className="metric-label">手续费</span>
                <span className="metric-value bad">{formatMinor(detail.data.fees, detail.data.currency)}</span>
              </div>
              <div className="metric">
                <span className="metric-label">应结净额</span>
                <span className="metric-value good">{formatMinor(detail.data.net, detail.data.currency)}</span>
              </div>
            </div>

            <p className="field-hint">
              批次号 <CopyId id={detail.data.id} full /> ·{' '}
              周期 {formatDate(detail.data.period_start)} → {formatDate(detail.data.period_end)} ·{' '}
              {detail.data.currency}
            </p>

            {detail.data.payouts.length > 0 && (
              <div className="stack tight">
                <h3>关联提现单</h3>
                <div className="table-scroll">
                  <table className="data">
                    <thead>
                      <tr><th>单号</th><th className="num">金额</th><th>方式</th><th>状态</th><th>出款时间</th></tr>
                    </thead>
                    <tbody>
                      {detail.data.payouts.map((payout) => (
                        <tr key={payout.id}>
                          <td><CopyId id={payout.id} /></td>
                          <td className="num"><Money value={payout.amount} currency={payout.currency} /></td>
                          <td>{payout.method}</td>
                          <td><StatusBadge status={payout.status} /></td>
                          <td className="cell-muted mono">{formatDateTime(payout.paid_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="stack tight">
              <h3>记账凭证</h3>
              <span className="field-hint">
                这是批次金额唯一可验证的来源 —— 按 `ref_id = 批次 id` 反查出来的真实分录。
              </span>
              {entries.error !== null ? <ErrorBox error={entries.error} />
                : entries.data === null ? <Loading />
                : entries.data.data.length === 0 ? (
                  <p className="field-hint">该批次还没有产生记账凭证（通常意味着尚未封账）。</p>
                ) : (
                  <div className="table-scroll">
                    <table className="data">
                      <thead>
                        <tr>
                          <th>凭证</th><th>账户</th><th className="num">借方</th><th className="num">贷方</th><th>时间</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entries.data.data.map((entry) => (
                          <tr key={entry.entry_id}>
                            <td><span className="cell-muted">{entry.txn_kind}</span></td>
                            <td>{entry.account_kind}</td>
                            <td className="num dr">
                              {entry.direction === 'dr' ? formatMinor(entry.amount, entry.currency) : '—'}
                            </td>
                            <td className="num cr">
                              {entry.direction === 'cr' ? formatMinor(entry.amount, entry.currency) : '—'}
                            </td>
                            <td className="cell-muted mono">{formatDateTime(entry.occurred_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
            </div>
          </>
        )}
    </Modal>
  );
}
